import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchGbpAudit,
  fetchGbpPerformance,
  isAuditedLocation,
  isPerformanceLocation,
  GbpAuditLocation,
  GbpAuditResponse,
  GbpGap,
  GbpKeyword,
  GbpMetricTotals,
  GbpMonthlyRow,
  GbpPerformanceLocation,
  GbpPerformanceResponse,
  GbpRange,
  GbpSectionError,
  GbpUnreadableLocation,
  KnownGbpGapCode,
} from "../../../services/gbpService";

/**
 * Google Business Profile section of the AI Visibility dashboard (SGL-328
 * Phase A: AC1 profile facts, AC3 performance, AC6 honest states).
 *
 * Rendered inside AiVisibilityDashboard's `.avm-scope`, so it reuses that
 * design system's classes and CSS variables (light + dark come for free); its
 * own additions are prefixed `avm-gbp-`.
 *
 * The same honesty rules as the rest of the dashboard apply, and matter more
 * here because Google itself withholds data:
 *   - A part we could not read shows "—" and says why. It is never a zero or
 *     an empty tile — "we couldn't read your reviews" is not "no reviews".
 *   - Keyword thresholds are "fewer than N", never a number to add up.
 *   - Q&A is gone from Google's API, so it says that, never "0 questions".
 *
 * Read-only (AC5): nothing here writes to the profile. The only actions are
 * links to the Connect page.
 */

/**
 * Google's editor stops at 750 characters, but Google does not officially
 * publish that limit, so it lives in one named place. The backend sends its
 * own copy (`description_max_length`); this is only the fallback.
 */
export const GBP_DESCRIPTION_MAX_LENGTH = 750;

const PERFORMANCE_MONTHS = 6;
const TOP_KEYWORDS = 10;
const LIST_PREVIEW = 3;
const CONNECT_HREF = "/social-posts?tab=connect";

/* ─────────────────────────── formatting ───────────────────────────────── */

const fmtNum = (n: number) => n.toLocaleString();
const plural = (n: number, one: string, many = `${one}s`) => `${fmtNum(n)} ${n === 1 ? one : many}`;

/** A share as a whole percent that never rounds a real share to 0% or 100%. */
function sharePct(part: number, whole: number): string {
  const pct = (part / whole) * 100;
  if (part > 0 && pct < 1) return "<1%";
  if (part < whole && pct > 99) return ">99%";
  return `${Math.round(pct)}%`;
}

/**
 * Profile views split by device, or null when there are no views to split
 * (a 0/0 share is not 0%). Google counts each view on exactly one device, so
 * mobile + desktop is all views.
 */
export function deviceSplit(t: Pick<GbpMetricTotals, "impressions_mobile" | "impressions_desktop">): {
  mobile: string;
  desktop: string;
} | null {
  const mobile = Math.max(0, t.impressions_mobile || 0);
  const desktop = Math.max(0, t.impressions_desktop || 0);
  const all = mobile + desktop;
  if (all <= 0) return null;
  const m = sharePct(mobile, all);
  const d = sharePct(desktop, all);
  // Rounded independently, an exact half rounds both up (101 of 200 -> 51% + 50%).
  // When both are plain whole percents, desktop is what mobile leaves.
  if (/^\d+%$/.test(m) && /^\d+%$/.test(d)) return { mobile: m, desktop: `${100 - parseInt(m, 10)}%` };
  return { mobile: m, desktop: d };
}

/** "YYYY-MM" → a local Date on the 1st (local, so no UTC day-shift). */
function monthDate(ym: string): Date | null {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
}

/** "Apr–Sep 2026", or "Nov 2025–Apr 2026" across a year boundary. */
export function rangeLabel(range: Pick<GbpRange, "start_month" | "end_month">): string {
  const start = monthDate(range.start_month);
  const end = monthDate(range.end_month);
  if (!start || !end) return "";
  const mon = (d: Date) => d.toLocaleDateString(undefined, { month: "short" });
  const monYear = (d: Date) => d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  if (start.getTime() === end.getTime()) return monYear(end);
  if (start.getFullYear() === end.getFullYear()) return `${mon(start)}–${monYear(end)}`;
  return `${monYear(start)}–${monYear(end)}`;
}

/** Google RFC 3339 timestamp → "Jan 3, 2026". Google may send nanosecond
 *  fractions; trim to milliseconds so Date parsing is not engine-dependent. */
function fmtTimestamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso.replace(/(\.\d{3})\d+/, "$1"));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Opening date is "YYYY-MM-DD", "YYYY-MM" or "YYYY" — Google's day and
 *  month are optional, and a missing one must not be shown as the 1st. */
function fmtOpeningDate(value: string | null): string | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y) return null;
  if (m && d) {
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  if (m) return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  return String(y);
}

function listPreview(items: string[]): string {
  const shown = items.slice(0, LIST_PREVIEW).join(", ");
  const rest = items.length - LIST_PREVIEW;
  return rest > 0 ? `${shown} +${rest} more` : shown;
}

function websiteHost(uri: string): string {
  try {
    return new URL(uri).host.replace(/^www\./, "");
  } catch {
    return uri;
  }
}

/* ─────────────────────────── human wording ────────────────────────────── */

/** One plain sentence per backend reason. The backend deliberately sends only
 *  a code (Google's raw message names our GCP project), so this is the text. */
export function sectionProblem(err: Pick<GbpSectionError, "reason" | "detail">): string {
  if (err.reason === "forbidden" && err.detail === "api_not_enabled") {
    return "The Google API for this isn't switched on for our app yet. That's on our side, not yours.";
  }
  switch (err.reason) {
    case "forbidden":
      return "Google refused access. The connected Google account may not manage this profile.";
    case "rate_limited":
      return "Google is limiting how often we can ask right now. Try again in a few minutes.";
    case "not_found":
      return "Google couldn't find this location. It may have been removed or moved to another account.";
    case "needs_reauth":
      return "Google needs you to reconnect before we can read this.";
    default:
      return "Google didn't answer properly just now. Try Refresh in a minute.";
  }
}

/** 2–4 word titles; the backend's `detail` sentence goes behind a disclosure. */
const GAP_TITLES: Record<KnownGbpGapCode, string> = {
  no_additional_categories: "Only one category",
  empty_description: "No description",
  no_service_items: "No services listed",
  no_service_area: "No service area",
  no_opening_date: "No opening date",
  no_website: "No website",
  no_phone: "No phone number",
  no_regular_hours: "No opening hours",
  no_reviews: "No reviews yet",
  reviews_without_reply: "Unanswered reviews",
  no_questions: "No questions yet",
  unanswered_questions: "Unanswered questions",
  no_posts: "No live posts",
  stale_posts: "No recent posts",
  no_photos: "No photos",
};

/** A code the backend adds later must render, not crash or vanish. */
export function gapTitle(code: string): string {
  return (GAP_TITLES as Record<string, string>)[code] ?? "Profile gap";
}

/* ─────────────────────────── styles ────────────────────────────────────── */

const GBP_STYLES = `
.avm-scope .avm-gbp-loc + .avm-gbp-loc { margin-top: 26px; }
.avm-scope .avm-gbp-loc-title { font-size: 15px; margin: 0 0 12px; }
.avm-scope .avm-gbp-refresh { margin-left: auto; font-family: inherit; font-size: 12px; font-weight: 600; color: var(--avm-muted); background: var(--avm-surface); border: 1px solid var(--avm-line); border-radius: 8px; padding: 6px 12px; cursor: pointer; }
.avm-scope .avm-gbp-refresh:disabled { opacity: .6; cursor: default; }
.avm-scope .avm-gbp-placeholder { min-height: 240px; display: grid; place-content: center; color: var(--avm-muted); }
.avm-scope .avm-gbp-error { border-color: var(--avm-bad); }
.avm-scope .avm-gbp-error p { margin: 0; color: var(--avm-bad); font-size: 14px; }
.avm-scope .avm-gbp-headline { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.avm-scope .avm-gbp-headline b { font-family: "Fraunces", serif; font-size: 40px; font-weight: 600; line-height: 1; }
.avm-scope .avm-gbp-headline span { font-size: 13px; color: var(--avm-muted); font-weight: 600; }
.avm-scope .avm-gbp-device { margin: -8px 0 16px; font-size: 13px; color: var(--avm-muted); font-weight: 600; font-variant-numeric: tabular-nums; }
.avm-scope .avm-gbp-device b { color: var(--avm-ink); }
.avm-scope .avm-gbp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(128px, 1fr)); gap: 12px; margin-bottom: 18px; }
.avm-scope .avm-gbp-kpis .avm-kpi { box-shadow: none; padding: 12px 14px; }
.avm-scope .avm-gbp-kpis .avm-kpi b { font-size: 22px; }
.avm-scope .avm-gbp-trend { margin-bottom: 18px; }
.avm-scope .avm-gbp-trend svg { width: 100%; max-width: 420px; height: auto; display: block; }
.avm-scope .avm-gbp-sub { font-size: 12px; font-weight: 700; color: var(--avm-muted); letter-spacing: .03em; margin: 0 0 6px; }
.avm-scope .avm-gbp-kw-count { font-size: 12px; color: var(--avm-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
.avm-scope .avm-gbp-threshold { cursor: help; border-bottom: 1px dotted var(--avm-faint); }
.avm-scope .avm-gbp-note { margin: 12px 0 0; font-size: 12.5px; color: var(--avm-muted); }
.avm-scope .avm-gbp-problem { margin: 4px 0 0; font-size: 13px; color: var(--avm-muted); }
.avm-scope .avm-gbp-rows { display: flex; flex-direction: column; }
.avm-scope .avm-gbp-row { display: grid; grid-template-columns: minmax(110px, 30%) 1fr; gap: 12px; padding: 9px 2px; font-size: 14px; }
.avm-scope .avm-gbp-row + .avm-gbp-row { border-top: 1px solid var(--avm-line); }
.avm-scope .avm-gbp-row dt { color: var(--avm-muted); font-weight: 600; font-size: 13px; }
.avm-scope .avm-gbp-row dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
.avm-scope .avm-gbp-row a { color: var(--avm-primary); }
.avm-scope .avm-gbp-dash { color: var(--avm-faint); font-weight: 700; margin-right: 6px; }
.avm-scope .avm-gbp-chip { display: inline-block; font-size: 12px; font-weight: 600; padding: 2px 9px; border-radius: 999px; margin: 0 6px 4px 0; background: var(--avm-surface-2); border: 1px solid var(--avm-line); color: var(--avm-ink); }
.avm-scope .avm-gbp-chip.primary { background: var(--avm-primary-soft); border-color: transparent; color: var(--avm-primary); }
.avm-scope .avm-gbp-gaps details { padding: 9px 2px; }
.avm-scope .avm-gbp-gaps details + details { border-top: 1px solid var(--avm-line); }
.avm-scope .avm-gbp-gaps summary { cursor: pointer; font-weight: 600; font-size: 14px; }
.avm-scope .avm-gbp-gaps details p { margin: 6px 0 0; font-size: 13px; color: var(--avm-muted); }
.avm-scope .avm-gbp-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
`;

/* ─────────────────────────── small building blocks ────────────────────── */

const Dash: React.FC = () => <span className="avm-gbp-dash" aria-hidden="true">—</span>;

/** "—" plus the reason, for anything we could not measure. */
const Unmeasured: React.FC<{ why: string }> = ({ why }) => (
  <>
    <Dash />
    <span className="avm-gbp-sr">Not available. </span>
    <span style={{ color: "var(--avm-muted)" }}>{why}</span>
  </>
);

const HalfError: React.FC<{ what: string }> = ({ what }) => (
  <div className="avm-card avm-gbp-error" role="alert">
    <p>We couldn't load your {what} from Google Business Profile. Try Refresh in a minute.</p>
  </div>
);

const Placeholder: React.FC = () => (
  <div className="avm-card avm-gbp-placeholder" aria-busy="true">Loading your Business Profile…</div>
);

/** Location-level problems that stop us reading it at all. */
const LocationNotice: React.FC<{ status: "needs_reauth" | "not_connected" | "fetch_failed" }> = ({ status }) => {
  if (status === "fetch_failed") {
    return (
      <div className="avm-notice">
        We couldn't reach Google for this profile just now. Try Refresh in a minute.
      </div>
    );
  }
  const reauth = status === "needs_reauth";
  return (
    <div className="avm-notice avm-notice-action">
      <div>
        <b>{reauth ? "Reconnect your Google Business Profile" : "Connect your Google Business Profile"}</b>
        <p>
          {reauth
            ? "Google's permission for this profile has expired or was removed, so we can't read it until you reconnect."
            : "This profile isn't fully connected, so we can't read it yet."}
        </p>
      </div>
      <a className="avm-run avm-run-alt" href={CONNECT_HREF}>{reauth ? "Reconnect" : "Connect"}</a>
    </div>
  );
};

/** Monthly profile views as small bars — no chart library for six numbers. */
const TrendBars: React.FC<{ monthly: GbpMonthlyRow[] }> = ({ monthly }) => {
  const W = 44;
  const H = 64;
  const views = monthly.map((r) => r.impressions_maps + r.impressions_search);
  const max = Math.max(1, ...views);
  const label = (r: GbpMonthlyRow) => {
    const d = monthDate(r.month);
    return d ? d.toLocaleDateString(undefined, { month: "short", year: "numeric" }) : r.month;
  };
  const summary = `Profile views by month: ${monthly
    .map((r, i) => `${label(r)} ${fmtNum(views[i])}`)
    .join(", ")}.`;
  return (
    <div className="avm-gbp-trend">
      <p className="avm-gbp-sub">Monthly trend</p>
      <svg viewBox={`0 0 ${monthly.length * W} ${H + 18}`} role="img" aria-label={summary}>
        {monthly.map((r, i) => {
          const h = Math.round((views[i] / max) * H);
          const d = monthDate(r.month);
          return (
            <g key={r.month}>
              <title>{`${label(r)}: ${fmtNum(views[i])} views`}</title>
              <rect x={i * W + 8} y={H - h} width={W - 16} height={Math.max(h, 1)} rx={3}
                fill={h > 0 ? "var(--avm-accent)" : "var(--avm-line)"} />
              <text x={i * W + W / 2} y={H + 13} textAnchor="middle" fontSize="10" fill="var(--avm-muted)">
                {d ? d.toLocaleDateString(undefined, { month: "short" }) : r.month}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const KeywordCount: React.FC<{ k: GbpKeyword }> = ({ k }) => {
  if (k.value !== null) return <span className="avm-gbp-kw-count">{fmtNum(k.value)}</span>;
  if (k.threshold !== null) {
    // Google only says "fewer than N" here. Shown as-is, never as a number.
    return (
      <span className="avm-gbp-kw-count">
        <span className="avm-gbp-threshold" title="Google hides exact counts below this">
          {`<${fmtNum(k.threshold)}`}
        </span>
        <span className="avm-gbp-sr"> (fewer than {fmtNum(k.threshold)}; Google hides exact counts below this)</span>
      </span>
    );
  }
  return <span className="avm-gbp-kw-count">—</span>;
};

/* ─────────────────────────── performance (AC3) ─────────────────────────── */

const PerformanceCard: React.FC<{ loc: GbpPerformanceLocation; range: GbpRange | null }> = ({ loc, range }) => {
  const period = range ? rangeLabel(range) : "";
  const t = loc.totals;
  const kw = loc.keywords;
  return (
    <div className="avm-card">
      <header>
        <h2>Performance</h2>
        {period && <span className="avm-meta">{period}</span>}
      </header>

      {t ? (
        <>
          <div className="avm-gbp-headline">
            <b className="avm-num">{fmtNum(t.impressions_maps + t.impressions_search)}</b>
            <span title="Times your profile was shown on Google Search and Maps. Google counts repeat views by the same person on the same day once.">
              profile views{period ? `, ${period}` : ""}
            </span>
          </div>
          {(() => {
            const split = deviceSplit(t);
            return split ? (
              <p className="avm-gbp-device" title={`${fmtNum(t.impressions_mobile)} on mobile, ${fmtNum(t.impressions_desktop)} on desktop`}>
                Mobile <b>{split.mobile}</b> · Desktop <b>{split.desktop}</b>
              </p>
            ) : null;
          })()}
          <div className="avm-gbp-kpis">
            <div className="avm-kpi"><b className="avm-num">{fmtNum(t.impressions_maps)}</b><div className="avm-lbl">Views on Maps</div></div>
            <div className="avm-kpi"><b className="avm-num">{fmtNum(t.impressions_search)}</b><div className="avm-lbl">Views on Search</div></div>
            <div className="avm-kpi"><b className="avm-num">{fmtNum(t.calls)}</b><div className="avm-lbl">Calls</div></div>
            <div className="avm-kpi"><b className="avm-num">{fmtNum(t.direction_requests)}</b><div className="avm-lbl">Direction requests</div></div>
            <div className="avm-kpi"><b className="avm-num">{fmtNum(t.website_clicks)}</b><div className="avm-lbl">Website clicks</div></div>
          </div>
          {loc.monthly.length > 1 && <TrendBars monthly={loc.monthly} />}
        </>
      ) : (
        <div className="avm-gbp-headline" style={{ display: "block" }}>
          <b aria-hidden="true">—</b>
          <p className="avm-gbp-problem">
            We couldn't read your profile views, calls and clicks.{" "}
            {sectionProblem(loc.metrics_error ?? { reason: "fetch_failed" })}
          </p>
        </div>
      )}

      <p className="avm-gbp-sub">Top searches that showed your profile</p>
      {kw.status === "ok" ? (
        kw.items.length ? (
          <>
            <div className="avm-src">
              {kw.items.slice(0, TOP_KEYWORDS).map((k, i) => (
                <div className="avm-src-row" key={`${k.keyword ?? ""}-${i}`}>
                  <span className="avm-name" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {k.keyword || "(unnamed search)"}
                  </span>
                  <KeywordCount k={k} />
                </div>
              ))}
            </div>
            {kw.truncated && (
              <p className="avm-gbp-note">Based on the first {fmtNum(kw.items.length)} searches Google returned.</p>
            )}
          </>
        ) : (
          <p className="avm-gbp-problem">Google reported no searches for this period.</p>
        )
      ) : (
        <p className="avm-gbp-problem"><Dash />{sectionProblem(kw)}</p>
      )}

      <p className="avm-gbp-note">Google's numbers can lag by a few days, so the latest month may still be settling.</p>
    </div>
  );
};

/* ─────────────────────────── profile snapshot (AC1) ────────────────────── */

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="avm-gbp-row">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const SnapshotCard: React.FC<{ loc: GbpAuditLocation }> = ({ loc }) => {
  const info = loc.business_info;
  const photos = loc.photos;
  const posts = loc.posts;
  const reviews = loc.reviews;
  const partial = [info, photos, posts, reviews].some((s) => s.status === "error");

  return (
    <div className="avm-card">
      <header><h2>Profile snapshot</h2></header>

      {info.status === "ok" && info.has_google_updated && (
        <div className="avm-notice">Google has changed parts of this profile itself. Check them in your Business Profile.</div>
      )}
      {info.status === "ok" && info.has_pending_edits && (
        <div className="avm-notice">Some edits are waiting on Google's review.</div>
      )}
      {partial && (
        <div className="avm-notice">We couldn't read every part of this profile. Those rows are marked —.</div>
      )}

      <dl className="avm-gbp-rows" style={{ margin: 0 }}>
        {info.status === "ok" ? (
          <>
            <Row label="Categories">
              {info.primary_category?.display_name || info.additional_categories.length ? (
                <>
                  {info.primary_category?.display_name && (
                    <span className="avm-gbp-chip primary" title="Primary category">
                      {info.primary_category.display_name}
                      <span className="avm-gbp-sr"> (primary)</span>
                    </span>
                  )}
                  {info.additional_categories.map((c, i) => (
                    <span className="avm-gbp-chip" key={`${c.id ?? ""}-${i}`}>{c.display_name || c.id}</span>
                  ))}
                </>
              ) : "Not set"}
            </Row>
            <Row label="Description">
              {info.description_length
                ? `${fmtNum(info.description_length)} / ${fmtNum(info.description_max_length || GBP_DESCRIPTION_MAX_LENGTH)} characters`
                : "Not set"}
            </Row>
            <Row label="Services">
              {info.service_items.length
                ? `${fmtNum(info.service_items.length)}: ${listPreview(info.service_items)}`
                : info.can_modify_service_list === false
                  ? "Google doesn't offer a service list for this category"
                  : "None listed"}
            </Row>
            <Row label="Service area">
              {info.service_area?.places.length
                ? listPreview(info.service_area.places)
                : info.service_area
                  ? "No places set"
                  : info.has_storefront_address
                    ? "None (customers visit your address)"
                    : "Not set"}
            </Row>
            <Row label="Opening hours">
              {info.regular_hours.length
                ? `Set, ${plural(new Set(info.regular_hours.map((p) => p.open_day)).size, "day")} a week` +
                  (info.special_hours_count ? ` · ${plural(info.special_hours_count, "special date")}` : "")
                : "Not set"}
            </Row>
            <Row label="Phone">{info.phone || "Not set"}</Row>
            <Row label="Website">
              {info.website_uri
                ? /^https?:\/\//i.test(info.website_uri)
                  ? <a href={info.website_uri} target="_blank" rel="noopener noreferrer">{websiteHost(info.website_uri)}</a>
                  : info.website_uri
                : "Not set"}
            </Row>
            <Row label="Opening date">{fmtOpeningDate(info.opening_date) || "Not set"}</Row>
          </>
        ) : (
          <Row label="Business details"><Unmeasured why={sectionProblem(info)} /></Row>
        )}

        <Row label="Photos">
          {photos.status === "ok" ? (
            photos.photo_count || photos.truncated ? (
              <>
                {photos.truncated ? `${fmtNum(photos.photo_count)}+ photos` : plural(photos.photo_count, "photo")}
                {photos.video_count > 0 && ` · ${plural(photos.video_count, "video")}`}
                {fmtTimestamp(photos.newest_create_time) &&
                  ` · newest upload ${fmtTimestamp(photos.newest_create_time)}` +
                    (photos.truncated ? ` (of the ${fmtNum(photos.items_scanned)} we checked)` : "")}
              </>
            ) : "No photos"
          ) : <Unmeasured why={sectionProblem(photos)} />}
        </Row>

        <Row label="Latest post">
          {posts.status === "ok" ? (
            fmtTimestamp(posts.latest_live_create_time)
              ? `${fmtTimestamp(posts.latest_live_create_time)}` +
                // Google's list order is undocumented: a capped scan's newest
                // is only the newest of what we saw.
                (posts.truncated ? ` (newest of the ${fmtNum(posts.count_seen)} we checked)` : "") +
                (posts.recurring_count ? ` · ${plural(posts.recurring_count, "recurring post")} live` : "")
              : posts.recurring_count
                ? `${plural(posts.recurring_count, "recurring post")} live`
                : posts.truncated
                  ? `No live post in the ${fmtNum(posts.count_seen)} we checked`
                  : "No live posts"
          ) : <Unmeasured why={sectionProblem(posts)} />}
        </Row>

        <Row label="Reviews">
          {reviews.status === "ok" ? (
            reviews.total_count ? (
              [
                reviews.average_rating !== null
                  ? `${reviews.average_rating.toLocaleString(undefined, { maximumFractionDigits: 1 })} ★`
                  : null,
                plural(reviews.total_count, "review"),
                reviews.reply_rate !== null
                  ? `replied to ${Math.round(reviews.reply_rate * 100)}% of the latest ${fmtNum(reviews.reviews_scanned)}`
                  : null,
              ].filter(Boolean).join(" · ")
            ) : "No reviews yet"
          ) : <Unmeasured why={sectionProblem(reviews)} />}
        </Row>

        <Row label="Q&A">
          {/* Google shut the Q&A API down on 2025-11-03. Unreadable is not zero. */}
          <span style={{ color: "var(--avm-muted)" }}>Google no longer provides Q&amp;A data</span>
        </Row>
      </dl>
    </div>
  );
};

const GapsCard: React.FC<{ gaps: GbpGap[]; partial: boolean }> = ({ gaps, partial }) => (
  <div className="avm-card">
    <header>
      <h2>Gaps</h2>
      {gaps.length > 0 && <span className="avm-meta">{plural(gaps.length, "gap")}</span>}
    </header>
    {gaps.length ? (
      <div className="avm-gbp-gaps">
        {gaps.map((g, i) => (
          <details key={`${g.code}-${i}`}>
            <summary>{gapTitle(g.code)}</summary>
            <p>{g.detail}</p>
          </details>
        ))}
      </div>
    ) : (
      <p className="avm-gbp-problem">
        {partial ? "No gaps found in the parts we could read." : "No gaps found."}
      </p>
    )}
  </div>
);

/* ─────────────────────────── one location ─────────────────────────────── */

interface Half<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
}

type TokenProblem = GbpUnreadableLocation["status"];

const LocationBlock: React.FC<{
  auditLoc?: GbpAuditLocation | GbpUnreadableLocation;
  perfLoc?: GbpPerformanceLocation | GbpUnreadableLocation;
  audit: Half<GbpAuditResponse>;
  perf: Half<GbpPerformanceResponse>;
  showTitle: boolean;
}> = ({ auditLoc, perfLoc, audit, perf, showTitle }) => {
  // A 401 on any call means the grant is dead for all of them, so a location
  // rolled up to needs_reauth is treated like one we never got a token for.
  const problems = new Set<TokenProblem>();
  if (auditLoc && (auditLoc.status === "needs_reauth" || !isAuditedLocation(auditLoc))) {
    problems.add(auditLoc.status as TokenProblem);
  }
  if (perfLoc && (perfLoc.status === "needs_reauth" || !isPerformanceLocation(perfLoc))) {
    problems.add(perfLoc.status as TokenProblem);
  }
  const notice: TokenProblem | null = problems.has("needs_reauth")
    ? "needs_reauth"
    : problems.has("not_connected")
      ? "not_connected"
      : problems.has("fetch_failed")
        ? "fetch_failed"
        : null;

  const auditData = auditLoc && isAuditedLocation(auditLoc) && auditLoc.status !== "needs_reauth" ? auditLoc : null;
  const perfData = perfLoc && isPerformanceLocation(perfLoc) && perfLoc.status !== "needs_reauth" ? perfLoc : null;
  const title = auditData?.title;

  // Slot content when this location has no readable data for a half.
  const fallback = (half: Half<unknown>, what: string, loc: unknown) => {
    if (loc) return null; // the location notice already explains it
    if (half.error) return <HalfError what={what} />;
    if (half.loading) return <Placeholder />;
    return null;
  };

  return (
    <div className="avm-gbp-loc">
      {showTitle && <h3 className="avm-gbp-loc-title">{title || "Business Profile location"}</h3>}
      {notice && <LocationNotice status={notice} />}

      <div style={{ marginBottom: 16 }}>
        {perfData ? <PerformanceCard loc={perfData} range={perf.data?.range ?? null} /> : fallback(perf, "performance numbers", perfLoc)}
      </div>

      {auditData ? (
        auditData.status === "error" ? (
          <div className="avm-card">
            <p className="avm-gbp-problem" style={{ marginTop: 0 }}>
              <Dash />We couldn't read any part of this profile from Google.{" "}
              {sectionProblem(auditData.business_info.status === "error" ? auditData.business_info : { reason: "fetch_failed" })}
            </p>
          </div>
        ) : (
          <div className="avm-grid avm-cols-2">
            <SnapshotCard loc={auditData} />
            <GapsCard gaps={auditData.gaps} partial={auditData.status === "partial"} />
          </div>
        )
      ) : (
        fallback(audit, "profile details", auditLoc)
      )}
    </div>
  );
};

/* ─────────────────────────── the section ──────────────────────────────── */

const idle = <T,>(loading: boolean): Half<T> => ({ loading, data: null, error: null });

const GbpProfileSection: React.FC<{ idToken: string | null | undefined; customerId: string | null | undefined }> = ({
  idToken,
  customerId,
}) => {
  const canLoad = Boolean(idToken && customerId);
  const [audit, setAudit] = useState<Half<GbpAuditResponse>>(() => idle(canLoad));
  const [perf, setPerf] = useState<Half<GbpPerformanceResponse>>(() => idle(canLoad));

  // Bumped on every load and on customer change/unmount. A response only lands
  // if its id is still current — otherwise customer A's slow response could
  // paint over customer B's section after a workspace switch.
  const requestId = useRef(0);

  const load = useCallback(() => {
    if (!idToken || !customerId) return;
    const id = ++requestId.current;
    setAudit((s) => ({ ...s, loading: true, error: null }));
    setPerf((s) => ({ ...s, loading: true, error: null }));

    // Independent on purpose: one failing must not blank the other.
    fetchGbpAudit(customerId, idToken).then(
      (data) => { if (requestId.current === id) setAudit({ loading: false, data, error: null }); },
      (e: unknown) => {
        if (requestId.current === id) {
          setAudit({ loading: false, data: null, error: (e as Error)?.message || "Failed to load" });
        }
      },
    );
    fetchGbpPerformance(customerId, idToken, PERFORMANCE_MONTHS).then(
      (data) => { if (requestId.current === id) setPerf({ loading: false, data, error: null }); },
      (e: unknown) => {
        if (requestId.current === id) {
          setPerf({ loading: false, data: null, error: (e as Error)?.message || "Failed to load" });
        }
      },
    );
  }, [idToken, customerId]);

  // A different customer's data must never show, even briefly, under the new one.
  useEffect(() => {
    setAudit(idle(canLoad));
    setPerf(idle(canLoad));
  }, [customerId, canLoad]);

  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const loading = audit.loading || perf.loading;
  const connectedTrue = audit.data?.connected === true || perf.data?.connected === true;
  const notConnected = !connectedTrue && (audit.data?.connected === false || perf.data?.connected === false);

  // Locations in audit order, then any only the performance call returned.
  const ids: string[] = [];
  for (const loc of [...(audit.data?.locations ?? []), ...(perf.data?.locations ?? [])]) {
    if (!ids.includes(loc.location_id)) ids.push(loc.location_id);
  }
  const blocks = ids.map((id) => ({
    id,
    auditLoc: audit.data?.locations.find((l) => l.location_id === id),
    perfLoc: perf.data?.locations.find((l) => l.location_id === id),
  }));

  // "Live" only when real Google data is actually on screen.
  const showsRealData = blocks.some(
    ({ auditLoc, perfLoc }) =>
      (auditLoc && isAuditedLocation(auditLoc) && (auditLoc.status === "ok" || auditLoc.status === "partial")) ||
      (perfLoc && isPerformanceLocation(perfLoc) && (perfLoc.status === "ok" || perfLoc.status === "partial")),
  );

  let body: React.ReactNode;
  if (!customerId) {
    body = null;
  } else if (notConnected) {
    body = (
      <div className="avm-card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <h3 style={{ fontSize: 17, marginBottom: 8 }}>Connect your Google Business Profile</h3>
        <p style={{ color: "var(--avm-muted)", maxWidth: "48ch", margin: "0 auto 16px" }}>
          See how often people find you on Google Maps and Search, what they searched for, and what your
          profile is missing. We only read your profile; nothing is changed.
        </p>
        <a className="avm-run avm-run-alt" href={CONNECT_HREF}>Connect Google Business Profile</a>
      </div>
    );
  } else if (blocks.length) {
    body = blocks.map((b) => (
      <LocationBlock key={b.id} auditLoc={b.auditLoc} perfLoc={b.perfLoc} audit={audit} perf={perf}
        showTitle={blocks.length > 1} />
    ));
  } else if (loading) {
    body = <Placeholder />;
  } else if (audit.error || perf.error) {
    body = (
      <>
        {perf.error && <div style={{ marginBottom: 16 }}><HalfError what="performance numbers" /></div>}
        {audit.error && <HalfError what="profile details" />}
      </>
    );
  } else if (connectedTrue) {
    // Connected, but no usable location in the stored connection.
    body = (
      <div className="avm-notice avm-notice-action">
        <div>
          <b>No Business Profile location found</b>
          <p>Your Google connection doesn't include a business location we can read. Reconnect and pick your location.</p>
        </div>
        <a className="avm-run avm-run-alt" href={CONNECT_HREF}>Reconnect</a>
      </div>
    );
  }

  return (
    <section aria-label="Google Business Profile">
      <style>{GBP_STYLES}</style>
      <div className="avm-section-label">
        <h2 style={{ fontSize: 17 }}>Google Business Profile</h2>
        {showsRealData && <span className="avm-live-tag">Live · Google Business Profile</span>}
        {/* Also offered after a failed call: every error message says "Try Refresh". */}
        {canLoad && !notConnected && (connectedTrue || audit.error || perf.error) && (
          <button type="button" className="avm-gbp-refresh" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>
      {body}
    </section>
  );
};

export default GbpProfileSection;
